/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { BaseService } from '@greenville/framework';
import omit from 'lodash/omit';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';
import { hasValidTrendsExportPathParams } from '../common/utils/auditExportModuleConfig';
import { handleAuditExportError, handleAuditExportResponse } from './auditExportResponseHandler';

const resolveEndpoint = (config, payload) => {
  if (config.endpoint) {
    return config.endpoint;
  }

  if (!config.pathPrefix || !config.pathParamKeys?.length) {
    throw new Error('Audit export module config is missing endpoint details');
  }

  if (!hasValidTrendsExportPathParams(config.moduleKey, payload)) {
    throw new Error('Invalid or missing export path parameters');
  }

  const pathSegments = config.pathParamKeys.map((key) => encodeURIComponent(payload[key]));
  return `${config.pathPrefix}/${pathSegments.join('/')}/export`;
};

const resolveFilenameSlug = (config, payload) =>
  typeof config.filenameSlug === 'function' ? config.filenameSlug(payload) : config.filenameSlug;

function createAuditExportService(moduleKey, moduleConfig) {
  return class AuditExportService extends BaseService {
    constructor() {
      super(moduleConfig.event, moduleConfig.statusStore);
      this.moduleConfig = { ...moduleConfig, moduleKey };
    }

    async handleEvent(eventName, event) {
      const { payload, headers } = utils.buildRequestPartsFromPayload(event);
      this.lastExportPayload = payload;

      const requestPayload = moduleConfig.stripKeys?.length ? omit(payload, moduleConfig.stripKeys) : payload;
      const endpoint = resolveEndpoint(this.moduleConfig, payload);
      const { isStackB } = utils.getStackBParams();
      const clients =
        typeof moduleConfig.resolveClients === 'function'
          ? moduleConfig.resolveClients(event)
          : { ...constants.AUDIT_EXPORT_DEFAULT_CLIENTS, ...moduleConfig.clients };

      if (isStackB) {
        const enhancedHeaders = await utils.resolveStackBHeaders(headers);
        return this.getClient(clients.stackB).post(endpoint, requestPayload, {
          headers: enhancedHeaders,
          responseType: 'blob'
        });
      }

      return this.getClient(clients.default).post(endpoint, requestPayload, {
        headers,
        responseType: 'blob'
      });
    }

    handleResponse(response) {
      const { tenantName } = this.lastExportPayload || {};
      const filename = utils.getAuditExportFilename(
        tenantName,
        resolveFilenameSlug(this.moduleConfig, this.lastExportPayload)
      );
      handleAuditExportResponse(response, filename);
    }

    async handleError(error) {
      await handleAuditExportError(error);
    }
  };
}

export default createAuditExportService;
